import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Spinner, ErrorBox, Success, Status, Mark, Risk, BudgetStatus, BudgetBar,
  Progress, fmtDate, num, money } from '../components/ui';

// Only the states a person can actually assert are offered. Outstanding
// and Delayed are consequences of the calendar, worked out for you, so
// they are shown but never selectable.
const STATUS_ACTIONS = [
  { code:'NOT_STARTED',    label:'Not started',    hint:'Clears the actual start and completion dates' },
  { code:'STARTED',        label:'Started',        hint:'Records today as the actual start' },
  { code:'COMPLETE',       label:'Completed',      hint:'Records the completion date' },
  { code:'NOT_APPLICABLE', label:'Not applicable', hint:'Excluded from progress' }
];

export default function ProjectPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const [project, setProject] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(0);
  const canInternal = can('remediation.internal');
  const canExternal = can('remediation.external');
  const allowedDisciplines = [canInternal && 'INTERNAL', canExternal && 'EXTERNAL'].filter(Boolean);
  const [discipline, setDiscipline] = useState(allowedDisciplines[0] || 'INTERNAL');
  const [saved, setSaved] = useState(null);
  const [users, setUsers] = useState([]);
  const editable = can('substage.edit.any') || can('substage.edit.assigned');

  const load = useCallback(() => api.get(`/projects/${id}`).then(r => setProject(r.data)).catch(setErr), [id]);
  useEffect(() => { setProject(null); load(); api.get('/users').then(r=>setUsers(r.data)).catch(()=>{}); }, [id, load]);

  if (err) return <ErrorBox error={err} />;
  if (!project) return <Spinner label="Loading project…" />;

  const disciplineStages = project.stages.filter(s => (s.discipline || 'INTERNAL') === discipline);
  const stage = disciplineStages[active] || disciplineStages[0];
  const flash = m => { setSaved(m); setTimeout(()=>setSaved(null), 2500); };

  const prog = project.progress || {};
  const budget = project.budget || {};
  const atRisk = prog.risk_level === 'At risk' || prog.risk_level === 'Overdue';
  const days = prog.days_to_expected_end;

  // The appointed organisations, carried down from the programme so
  // whoever opens a site can see who is responsible without going back up.
  const appointments = [
    ['Main contractor', project.contractor_name],
    ['BRPD', project.brpd_name],
    ['Fire engineer', project.fire_engineer_name],
    ['Architect / lead designer', project.architect_name],
    ['Drylining consultant', project.drylining_name]
  ];

  return <>
    <div className="phead">
      <div>
        <h1 style={{marginBottom:2}}>{project.name}</h1>
        <div className="muted">
          <Link to={`/programmes/${project.programme_id}`}>{project.programme_name}</Link>
          {' · '}{project.reference}
          {' · '}{project.buildings.map(b => `${b.name} (${b.building_type})`).join(', ')}
        </div>
      </div>
      <div style={{display:'flex',gap:8,alignItems:'center'}}>
        {can('expenditure.view') &&
          <Link className="btn" to={`/projects/${id}/expenditure`}>Budget &amp; expenditure</Link>}
      </div>
    </div>

    <Success message={saved} />

    {/* Early warning: the expected end date is close, or already gone. */}
    {atRisk && (
      <div className={`alert ${prog.risk_level === 'Overdue' ? 'error' : 'warn'}`}>
        <strong>{prog.risk_level === 'Overdue' ? 'Past its expected end date.' : 'Approaching its expected end date.'}</strong>{' '}
        This project is due to finish on {fmtDate(prog.expected_end_date)}
        {days == null ? '' : days < 0 ? `, which was ${Math.abs(days)} day${Math.abs(days)===1?'':'s'} ago`
                                      : `, in ${days} day${days===1?'':'s'}`}
        {' '}and is {Number(prog.pct_by_count||0).toFixed(0)}% complete
        {Number(prog.delayed_substages) > 0 ? `, with ${num(prog.delayed_substages)} task${Number(prog.delayed_substages)===1?'':'s'} already delayed` : ''}.
      </div>
    )}

    <div className="stat-grid">
      <div className="stat static">
        <div className="label">Project status</div>
        <div className="value" style={{fontSize:19,paddingTop:4}}><Status value={prog.progress_status} /></div>
        <div className="sub">{Number(prog.pct_by_count||0).toFixed(0)}% complete · {num(prog.complete_substages)} of {num(prog.applicable_substages)} tasks</div>
      </div>
      <div className="stat static">
        <div className="label">Delivery risk</div>
        <div className="value" style={{fontSize:19,paddingTop:4}}><Risk value={prog.risk_level} /></div>
        <div className="sub">Due {fmtDate(prog.expected_end_date)}{days == null ? '' : days < 0 ? ` · ${Math.abs(days)}d ago` : ` · in ${days}d`}</div>
      </div>
      <div className="stat static"><div className="label">Progress (by task)</div><div className="value">{Number(prog.pct_by_count||0).toFixed(0)}%</div><div className="sub">{num(prog.complete_substages)} of {num(prog.applicable_substages)} tasks</div></div>
      <div className="stat static"><div className="label">Progress (by duration)</div><div className="value">{Number(prog.pct_by_duration||0).toFixed(0)}%</div><div className="sub">effort-weighted</div></div>
      <div className="stat static over"><div className="label">Delayed tasks</div><div className="value">{num(prog.delayed_substages)}</div><div className="sub">{num(prog.outstanding_substages)} outstanding</div></div>
      <div className="stat static"><div className="label">Buildings</div><div className="value">{project.buildings.length}</div><div className="sub">{project.buildings.filter(b=>b.building_type==='HRB').length} HRB / {project.buildings.filter(b=>b.building_type==='LRB').length} LRB</div></div>
    </div>

    <div style={{display:'grid', gridTemplateColumns:'1.15fr 1fr', gap:16, alignItems:'start'}}>
      <div className="panel">
        <h3>Appointments</h3>
        <table className="data"><tbody>
          {appointments.map(([role, org]) => (
            <tr key={role}>
              <td style={{width:'46%', color:'var(--muted)'}}>{role}</td>
              <td>{org || <span className="muted">Not appointed</span>}</td>
            </tr>
          ))}
          <tr><td style={{color:'var(--muted)'}}>Client</td><td>{project.client_name}</td></tr>
        </tbody></table>
      </div>

      {can('expenditure.view') && (
        <div className="panel">
          <div className="phead"><h3 style={{margin:0}}>Budget</h3>
            <BudgetStatus value={budget.budget_status} /></div>
          <table className="data"><tbody>
            <tr><td style={{width:'46%',color:'var(--muted)'}}>Budgeted</td><td><strong>{money(budget.budget_amount)}</strong></td></tr>
            <tr><td style={{color:'var(--muted)'}}>Total spent</td><td>{money(budget.total_spent)}</td></tr>
            <tr><td style={{color:'var(--muted)'}}>Balance</td>
                <td className={Number(budget.balance) < 0 ? 'neg' : ''}>{money(budget.balance)}</td></tr>
            <tr><td style={{color:'var(--muted)'}}>Spent</td>
                <td><BudgetBar pct={budget.pct_spent} status={budget.budget_status} /></td></tr>
          </tbody></table>
          <div style={{marginTop:10}}>
            <Link className="btn" to={`/projects/${id}/expenditure`}>
              {can('expenditure.create') ? 'Record expenditure' : 'View expenditure'}
            </Link>
          </div>
        </div>
      )}
    </div>

    <div className="panel" style={{padding:0}}>
      {allowedDisciplines.length > 1 && (
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px 0', borderBottom: '1px solid var(--line)' }}>
          {allowedDisciplines.map(disc => (
            <button key={disc} onClick={() => { setDiscipline(disc); setActive(0); }}
              className={discipline === disc ? 'primary' : ''} style={{ fontSize: 12.5 }}>
              {disc === 'INTERNAL' ? 'Internal remediation' : 'External remediation'}
            </button>
          ))}
        </div>
      )}
      {disciplineStages.length === 0 ? (
        <div style={{ padding: 24 }} className="muted">
          No {discipline.toLowerCase()} stages exist for this project yet. External remediation stages will be added in a future update.
        </div>
      ) : (<>
      {/* Every stage carries a green tick when complete, a red cross when
          not, so the whole plan reads at a glance from the tab strip. */}
      <div className="tabs">
        {disciplineStages.map((s, i) => (
          <div key={s.id} className={`tab${i===active?' active':''}`} onClick={()=>setActive(i)}
               title={`${s.stage_name}: ${s.status_name}`}>
            <Mark done={!!s.is_complete} size={13} title={s.status_name} />
            <span style={{marginLeft:5}}>{s.name}</span>
          </div>
        ))}
      </div>

      {/* A stage-by-stage summary of the same ticks and crosses, so the
          completed and incomplete stages can be read as one list rather
          than by clicking through every tab. */}
      <div style={{padding:'12px 16px 0'}}>
        <div className="stage-strip">
          {disciplineStages.map((s, i) => (
            <button key={s.id} type="button"
              className={`stage-chip${i===active?' active':''}`} onClick={()=>setActive(i)}>
              <Mark done={!!s.is_complete} size={13} title={s.status_name} />
              <span className="stage-chip-name">{s.stage_code ? `${s.stage_code} · ` : ''}{s.name}</span>
              <span className="stage-chip-count">{num(s.complete_substages)}/{num(s.applicable_substages)}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{padding:'16px'}}>
        <div className="phead">
          <div>
            <h2 style={{marginBottom:2, display:'flex', alignItems:'center', gap:8}}>
              <Mark done={!!stage.is_complete} size={17} title={stage.status_name} />
              {stage.name}
            </h2>
            <div className="muted">{num(stage.complete_substages)} of {num(stage.applicable_substages)} tasks complete · <Status value={stage.status_name} /></div>
          </div>
          <div className="muted">
            Start date {fmtDate(stage.start_date)} · Expected end date {fmtDate(stage.expected_end_date)}
            {stage.completed_on ? <> · Completed {fmtDate(stage.completed_on)}</> : null}
          </div>
        </div>
        <div style={{marginBottom:12}}><Progress value={stage.pct_complete} /></div>

        {stage.substages.map(sub => (
          <SubstageCard key={sub.substage_id} sub={sub} users={users} editable={editable}
            onSaved={() => { flash('Saved. The status was worked out from the dates.'); load(); }} onError={setErr} />
        ))}
      </div>
      </>)}
    </div>
  </>;
}

function SubstageCard({ sub, users, editable, onSaved, onError }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    startDate: sub.start_date || '', expectedEndDate: sub.expected_end_date || '',
    actualStart: sub.actual_start || '', completedOn: sub.completed_on || '',
    statusCode: derivedAction(sub), assignedTo: sub.assigned_to || ''
  });
  const [busy, setBusy] = useState(false);

  // Map the derived status onto the action that produced it, so the
  // editor opens showing what the user last did rather than the
  // calendar's verdict on it.
  function derivedAction(s) {
    if (s.status_code === 'NOT_APPLICABLE') return 'NOT_APPLICABLE';
    if (s.is_complete) return 'COMPLETE';
    if (s.actual_start) return 'STARTED';
    return 'NOT_STARTED';
  }

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/substages/${sub.substage_id}`, {
        startDate: form.startDate || null, expectedEndDate: form.expectedEndDate || null,
        actualStart: form.actualStart || null, completedOn: form.completedOn || null,
        statusCode: form.statusCode, assignedTo: form.assignedTo || null,
        rowVersion: sub.row_version
      });
      setOpen(false); onSaved();
    } catch (e) { onError(e); setBusy(false); }
  };

  return (
    <div className="panel" style={{marginBottom:10, padding:12}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:10}}>
        <div style={{fontWeight:600, fontSize:13, display:'flex', alignItems:'center', gap:7}}>
          <Mark done={!!sub.is_complete} size={14} title={sub.status_name} />
          <span>
            {sub.substage_name}
            {sub.destination_note ? <span className="muted" style={{fontWeight:400}}> · {sub.destination_note}</span> : null}
            {sub.building_name ? <span className="muted" style={{fontWeight:400}}> · {sub.building_name}</span> : null}
          </span>
        </div>
        <div style={{display:'flex', gap:10, alignItems:'center'}}>
          <Status value={sub.status_name} />
          {editable && <button onClick={()=>setOpen(o=>!o)}>{open?'Close':'Edit'}</button>}
        </div>
      </div>

      {!open ? (
        <div className="muted" style={{marginTop:8, display:'flex', gap:18, flexWrap:'wrap'}}>
          <span>Start date: {fmtDate(sub.start_date)}</span>
          <span>Expected end date: {fmtDate(sub.expected_end_date)}</span>
          <span>Actually started: {fmtDate(sub.actual_start)}</span>
          <span>Completed: {fmtDate(sub.completed_on)}</span>
          <span>Assigned: {sub.assigned_name || 'Unassigned'}{sub.assigned_org?` · ${sub.assigned_org}`:''}</span>
          {sub.days_overdue > 0 ? <span className="neg">{sub.days_overdue}d past expected end</span> : null}
          {sub.days_unstarted > 0 ? <span className="warn-text">unstarted for {sub.days_unstarted}d</span> : null}
          {sub.finish_variance_days > 0 ? <span className="neg">finished {sub.finish_variance_days}d late</span> : null}
        </div>
      ) : (
        <div style={{marginTop:10}}>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10}}>
            <div><label>Start date</label><input type="date" style={{width:'100%'}} value={form.startDate} onChange={e=>setForm({...form,startDate:e.target.value})}/></div>
            <div><label>Expected end date</label><input type="date" style={{width:'100%'}} value={form.expectedEndDate} onChange={e=>setForm({...form,expectedEndDate:e.target.value})}/></div>
            <div><label>Actually started</label><input type="date" style={{width:'100%'}} value={form.actualStart} onChange={e=>setForm({...form,actualStart:e.target.value})}/></div>
            <div><label>Completed on</label><input type="date" style={{width:'100%'}} value={form.completedOn} onChange={e=>setForm({...form,completedOn:e.target.value})}/></div>
            <div><label>Mark as</label><select style={{width:'100%'}} value={form.statusCode} onChange={e=>setForm({...form,statusCode:e.target.value})}>
              {STATUS_ACTIONS.map(o=><option key={o.code} value={o.code}>{o.label}</option>)}</select></div>
            <div><label>Assigned to</label><select style={{width:'100%'}} value={form.assignedTo} onChange={e=>setForm({...form,assignedTo:e.target.value})}>
              <option value="">Unassigned</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.full_name} · {u.organisation}</option>)}</select></div>
          </div>
          <div className="alert info" style={{marginTop:10, marginBottom:10}}>
            The status is worked out from these dates, not chosen. Once the start date
            arrives with no actual start recorded the task reads <strong>Outstanding</strong>;
            the day after it starts it becomes <strong>In progress</strong>; and if the expected
            end date passes before it finishes it becomes <strong>Delayed</strong>. Completion is
            stamped with your name automatically.
          </div>
          <div style={{display:'flex', gap:8}}>
            <button className="primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save'}</button>
            <button onClick={()=>setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
